// Nutrition Analysis Cache
// BUG FIX D1 V10: Ensures connector invariance by caching individual item parsing results
//
// FE HARDENING (v2):
// - LRU eviction: on cache hit, delete + re-insert to move to end of Map iteration order.
// - Quantity-normalised keys: "tuna 80g" and "80g tuna" produce the same cache key.
// - Max cache size: 300 entries (prevents unbounded memory growth in long sessions).

import type { NutritionExtraction } from '../services/openai/nutrition.service';

interface CacheEntry {
  result: NutritionExtraction;
  timestamp: number;
}

// In-memory cache (30 day TTL for restaurant, 7 day for regular items)
const CACHE_TTL_RESTAURANT = 30 * 24 * 60 * 60 * 1000; // 30 days
const CACHE_TTL_REGULAR = 7 * 24 * 60 * 60 * 1000; // 7 days
const CACHE_MAX_SIZE = 300;

const cache = new Map<string, CacheEntry>();

/**
 * Normalize item description for cache key.
 * Produces identical keys for equivalent inputs:
 *   "Tuna 80g" → "80g tuna"
 *   "80 g tuna" → "80g tuna"
 *   "TUNA  80G" → "80g tuna"
 *
 * Strategy: extract all quantity tokens, sort them, prepend to sorted food words.
 */
function normalizeForCacheKey(description: string): string {
  const lower = description
    .toLowerCase()
    .trim()
    .replace(/[^\w\s\d.]/g, '') // Remove punctuation (keep decimals)
    .replace(/\s+/g, ' ')
    .trim();

  // Extract quantity tokens: "80g", "250ml", "1.5kg", etc.
  const quantityPattern = /(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb|tsp|tbsp|cups?|slices?|pieces?)\b/gi;
  const quantities: string[] = [];
  let cleaned = lower;

  let match;
  while ((match = quantityPattern.exec(lower)) !== null) {
    // Normalise: collapse space between number and unit
    quantities.push(`${match[1]}${match[2].replace(/s$/, '')}`);
  }
  // Remove quantity tokens from the text
  cleaned = cleaned.replace(quantityPattern, '').replace(/\s+/g, ' ').trim();

  // Sort remaining food words alphabetically
  const foodWords = cleaned.split(/\s+/).filter(w => w.length > 0).sort();

  // Sort quantities for consistency
  quantities.sort();

  // Combine: quantities first, then food words
  return [...quantities, ...foodWords].join(' ').trim();
}

/**
 * Generate cache key for nutrition analysis
 */
export function getCacheKey(
  textDescription: string,
  isRestaurant: boolean = false,
  userId?: string,
): string {
  const normalized = normalizeForCacheKey(textDescription);
  const mode = isRestaurant ? 'restaurant' : 'regular';
  const user = userId || 'anonymous';
  return `${mode}:${user}:${normalized}`;
}

/**
 * Get cached result if available and not expired.
 * LRU: on hit, delete and re-insert to move to end of Map iteration order.
 */
export function getCachedResult(
  cacheKey: string,
  isRestaurant: boolean = false,
): NutritionExtraction | null {
  const entry = cache.get(cacheKey);
  if (!entry) return null;
  
  const ttl = isRestaurant ? CACHE_TTL_RESTAURANT : CACHE_TTL_REGULAR;
  const age = Date.now() - entry.timestamp;
  
  if (age > ttl) {
    cache.delete(cacheKey);
    return null;
  }
  
  // LRU: move to end of Map iteration order
  cache.delete(cacheKey);
  cache.set(cacheKey, entry);

  return entry.result;
}

/**
 * Store result in cache.
 * LRU eviction: remove oldest entry when cache exceeds max size.
 */
export function setCachedResult(cacheKey: string, result: NutritionExtraction): void {
  // LRU eviction: remove oldest (first) entry
  if (cache.size >= CACHE_MAX_SIZE) {
    const oldest = cache.keys().next().value;
    if (oldest) cache.delete(oldest);
  }

  cache.set(cacheKey, {
    result: { ...result }, // Deep copy to prevent mutation
    timestamp: Date.now(),
  });
}

/**
 * Clear cache (for testing or manual invalidation)
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Get cache stats (for debugging)
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()),
  };
}
